import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Calendar, ExternalLink, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtLong(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

function fmtShort(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export function InsightsList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/blog/posts`)
      .then(({ data }) => { if (!cancelled) { setPosts(data.posts || []); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.detail || "Could not load insights."); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Insights - All Things Social Impact",
    url: "https://allthingssocialimpact.com/insights",
    blogPost: posts.slice(0, 20).map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      datePublished: p.date,
      url: `https://allthingssocialimpact.com/insights/${p.slug}`,
      author: { "@type": "Person", name: "Andrea Kanaris" },
    })),
  };

  return (
    <div data-testid="insights-page">
      <Seo
        title="Insights & Thought Leadership"
        description="Executive briefings, comparative analysis, and pointed observations on how social risk shows up in real projects. Written by Andrea Kanaris."
        path="/insights"
        jsonLd={jsonLd}
      />
      <section className="bg-brand-blue text-white py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="eyebrow text-brand-yellow mb-6">Insights & thought leadership</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">Fieldnotes from the practice.</h1>
          <p className="text-lg lg:text-xl text-white/80 max-w-3xl mt-6 leading-relaxed">Executive briefings, comparative analysis, and pointed observations on how social risk shows up in real projects - Australian and US. Written by Andrea Kanaris.</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="py-24 flex items-center justify-center text-slate-500" data-testid="insights-loading"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading insights…</div>
          ) : error ? (
            <div className="py-24 text-center text-red-700" data-testid="insights-error">{error}</div>
          ) : posts.length === 0 ? (
            <div className="py-24 text-center text-slate-500" data-testid="insights-empty">No insights published yet.</div>
          ) : (
            <div className="divide-y divide-slate-200">
              {posts.map((it) => (
                <Link key={it.slug} to={`/insights/${it.slug}`} data-testid={`insight-${it.slug}`} className="block py-8 group">
                  <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-10">
                    <div className="md:w-40 flex-shrink-0">
                      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-orange">{it.tag}</div>
                      <div className="text-xs text-slate-500 mt-2 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {fmtShort(it.date)}</div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif text-2xl text-brand-blue leading-snug mb-2 group-hover:text-brand-orange transition-colors">{it.title}</h3>
                      <p className="text-slate-600 leading-relaxed">{it.excerpt}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-brand-orange transition-colors flex-shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function InsightDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setNotFound(false); setPost(null); setRelated([]);
    axios.get(`${API}/blog/posts/${slug}`)
      .then(({ data }) => {
        if (cancelled) return;
        setPost(data);
        setLoading(false);
        // Fetch the full list to build "You might also like".
        axios.get(`${API}/blog/posts`)
          .then(({ data: list }) => {
            if (cancelled) return;
            const others = (list.posts || []).filter((p) => p.slug !== data.slug);
            const sameTag = others.filter((p) => p.tag === data.tag);
            const rest = others.filter((p) => p.tag !== data.tag);
            setRelated([...sameTag, ...rest].slice(0, 3));
          })
          .catch(() => {});
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 404) setNotFound(true);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-slate-500" data-testid="insight-loading">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
    </div>
  );

  if (notFound || !post) return (
    <div className="py-32 text-center" data-testid="insight-not-found">
      <Seo title="Insight not found" path={`/insights/${slug}`} noindex />
      <h1 className="font-serif text-3xl text-brand-blue">Insight not found</h1>
      <Link to="/insights" className="text-brand-orange text-xs uppercase tracking-widest font-semibold hover:underline mt-6 inline-block">← Back to all insights</Link>
    </div>
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.meta_description || post.excerpt,
    datePublished: post.date,
    url: `https://allthingssocialimpact.com/insights/${post.slug}`,
    author: { "@type": "Person", name: "Andrea Kanaris" },
    publisher: { "@type": "Organization", name: "All Things Social Impact" },
    ...(post.cover_image_url ? { image: post.cover_image_url } : {}),
    articleSection: post.tag,
  };

  return (
    <div data-testid={`insight-detail-${slug}`}>
      <Seo
        title={post.title}
        description={post.meta_description || post.excerpt}
        path={`/insights/${post.slug}`}
        image={post.cover_image_url}
        ogType="article"
        publishedTime={post.date}
        articleTag={post.tag}
        jsonLd={jsonLd}
      />
      <section className="bg-brand-blue text-white py-20 lg:py-28">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <Link to="/insights" className="text-brand-yellow text-xs uppercase tracking-[0.2em] font-semibold hover:underline">← All insights</Link>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-yellow mt-6 mb-4">{post.tag}</div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">{post.title}</h1>
          <div className="text-sm text-white/60 mt-6 flex flex-wrap items-center gap-4">
            <span>{fmtLong(post.date)}</span>
            <span className="text-white/40">·</span>
            <span>By Andrea Kanaris</span>
          </div>
        </div>
      </section>
      {post.cover_image_url && (
        <div className="max-w-[900px] mx-auto -mt-8 relative z-10 px-6 lg:px-10">
          <img src={post.cover_image_url} alt="" data-testid="insight-cover" className="w-full h-auto border border-slate-200 shadow-lg" />
        </div>
      )}
      <section className="py-16 lg:py-20 bg-white">
        <article className="max-w-[780px] mx-auto px-6 lg:px-10">
          <p data-testid="insight-lede" className="text-xl text-slate-700 leading-relaxed font-serif italic mb-10 pb-8 border-b border-slate-200">{post.excerpt}</p>
          <div data-testid="insight-body" className="insight-prose">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body || ""}</ReactMarkdown>
          </div>
          {post.source_url && (
            <div className="mt-14 pt-8 border-t border-slate-200 text-sm text-slate-500">
              <a href={post.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-brand-orange font-semibold hover:underline">
                Read the original on our WordPress blog <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}
        </article>
      </section>

      {related.length > 0 && (
        <section data-testid="insight-related" className="bg-slate-50 border-t border-slate-200 py-16 lg:py-20">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
            <div className="flex items-baseline justify-between mb-8 gap-4 flex-wrap">
              <div>
                <div className="eyebrow text-brand-orange mb-2">You might also like</div>
                <h2 className="font-serif text-2xl lg:text-3xl text-brand-blue">More from the practice.</h2>
              </div>
              <Link to="/insights" data-testid="insight-related-all-link" className="link-arrow text-sm">
                All insights <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/insights/${r.slug}`}
                  data-testid={`insight-related-${r.slug}`}
                  className="group block bg-white border border-slate-200 p-6 hover:border-brand-orange hover:shadow-md transition-all h-full"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-orange mb-3">{r.tag}</div>
                  <h3 className="font-serif text-lg text-brand-blue leading-snug mb-3 group-hover:text-brand-orange transition-colors">{r.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 mb-4">{r.excerpt}</p>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> {fmtShort(r.date)}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
